import { ParsedUrlQuery } from 'querystring';

import ErrorPage from 'next/error';
import { GetStaticPaths, GetStaticProps } from 'next';

import ArticleTile from '@components/Resources/Home/ArticleTile';
import CategoryNavigation from '@components/Resources/Category/CategoryNavigation';
import { ReferencesRibbon } from '@common/ReferencesRibbon';
import { Footer } from '@components/Footer/Footer';
import Meta from '@components/Meta';
import { NavBar } from '@components/NavBar/NavBar';
import ArticleNextButton from '@components/Resources/Article/ArticleNextButton';

import {
  combinePostsForCountryVariants,
  getEmptyStaticPaths,
  getRecentArticles,
  postGuidesQuery,
} from '@nextUtils/guidesUtils';

import { H2, H4, H5 } from '@common/headline';
import { SignPost } from '@common/SignPost';

import {
  ArticleCategoriesType,
  CategoryPageInfoType,
  RecentArticleEdgeType,
  CategoryFieldsFragment,
} from '@nextTypes/guides';

import { ErrorTracking } from '@common/errorTracking';

type Props = {
  afterCursor: string | null;
  allCategories: ArticleCategoriesType;
  categorySlug: string;
  parentCategorySlug: string | null;
  recentPosts: {
    edges: RecentArticleEdgeType[];
    pageInfo: CategoryPageInfoType;
  };
};

const POSTS_PER_PAGE = 10;

export const getStaticPaths: GetStaticPaths = getEmptyStaticPaths;

interface Params extends ParsedUrlQuery {
  categorySlugs: string | string[];
}

export const getStaticProps: GetStaticProps<Props, Params> = async ({
  params: { categorySlugs } = {},
  locale,
}) => {
  try {
    if (!Array.isArray(categorySlugs) || categorySlugs.length === 0) {
      throw new Error('Query category slugs is not an array');
    }

    let afterCursor: string | null = null;
    const containsAfterCursor = categorySlugs.some(slug => slug === 'after');
    if (containsAfterCursor) {
      afterCursor = categorySlugs[categorySlugs.length - 1];
    }

    // NB we are allowed to use the slug as the search term for posts(where: {categoryName: ...})
    const categorySlug = containsAfterCursor
      ? categorySlugs[categorySlugs.length - 3]
      : categorySlugs[categorySlugs.length - 1];

    // Only set if subcategory exists
    const parentCategorySlug =
      categorySlugs.length === 2 ||
      (containsAfterCursor && categorySlugs.length === 4)
        ? categorySlugs[0]
        : null;

    const recentCategoryName = categorySlug === 'all' ? '' : categorySlug;

    // There can be a maximum of 2 country variants per post - one for all countries and one for this locale -
    // so request double the number of posts before combining them
    const paginationArgs = `first: ${POSTS_PER_PAGE * 2},
      ${afterCursor ? `after: "${afterCursor}",` : ''}`;

    const query = `
      ${CategoryFieldsFragment}
      query {
        allCategories: categories(first: 100, where: {shouldOutputInFlatList: true}) {
          edges {
            node {
              ...CategoryFields
              description
            }
          }
        }
        recentPosts: posts(${paginationArgs}
          where: {
          categoryName: "${recentCategoryName}"
          categoryNotIn: [1, 56, 57, 59], # 1 = Uncategorised, 56 = Stories, 57 = Local, 59 - Blog
          orderby:{
            field:DATE
            order:DESC
          }
          tagSlugIn: [ "all_countries", "${locale}" ]
        }) {
          pageInfo {
            endCursor
            hasNextPage
          }
          edges {
            cursor
            node {
              title
              slug
              seo {
                metaDesc
              }
              author {
                name
                avatar {
                  url
                }
              }
              categories(where: {shouldOutputInFlatList: true}) {
                edges {
                  node {
                    ...CategoryFields
                  }
                }
              }
              featuredImage {
                mediaDetails {
                  sizes {
                    name
                    sourceUrl
                  }
                }
              }
            }
          }
        }
      }
    `;

    const json = await postGuidesQuery(query);

    return {
      props: {
        afterCursor,
        allCategories: json.data.allCategories,
        categorySlug,
        parentCategorySlug,
        recentPosts: combinePostsForCountryVariants(
          json.data.recentPosts,
          POSTS_PER_PAGE,
        ),
      },
      // Refresh this page at most every 300 seconds
      // If a request comes in within 300 seconds, it will serve the existing cached page
      // If a request comes in outside of 300 seconds, it will serve the cached page - but re-fetch the page in the background
      revalidate: 300,
    };
  } catch (err) {
    ErrorTracking.track(err);
    // We do not handle this error, but it is handled by NextJS https://github.com/vercel/next.js/discussions/11180#discussioncomment-2399
    throw err;
  }
};

const Category = ({
  afterCursor,
  allCategories,
  categorySlug,
  parentCategorySlug,
  recentPosts,
}: Props): JSX.Element => {
  let title, metaDescription, categoryName, comingSoonArticleType;

  if (categorySlug === 'all') {
    title = 'The Latest Evidence-Based Health Resources';
    metaDescription =
      'Knowledge is power. Get all the information you need to get on track – and stay there.';
    categoryName = 'Latest Articles';
    comingSoonArticleType = 'Articles';
  } else {
    const categoryEdge = allCategories.edges.find(
      edge => edge.node.slug === categorySlug,
    );

    if (!categoryEdge) {
      return <ErrorPage statusCode={404} />;
    }

    metaDescription = categoryEdge.node.description;
    categoryName = categoryEdge.node.name;

    if (
      categoryEdge.node.parent &&
      categoryEdge.node.parent.slug === 'recipes'
    ) {
      title = `Simple, Healthy ${categoryName} Recipes`;
      comingSoonArticleType = 'Recipes';
    } else {
      comingSoonArticleType = 'Articles';
      switch (categorySlug) {
        case 'diabetes':
          title = 'The Latest Diabetes Research';
          break;
        case 'exercise':
          title = 'Evidence-Based Exercise Research';
          break;
        case 'habit-change':
          title = 'Change Unhealthy Habits';
          break;
        case 'mind':
          title = 'Health and the Mind';
          break;
        case 'mindfulness':
          title = 'The Importance of Mindfulness';
          break;
        case 'motivation':
          title = 'Finding the Motivation for Lifestyle Change';
          break;
        case 'myth-busting':
          title = 'Evidence-Based Myth-Busting Articles';
          break;
        case 'nutrition':
          title = 'The Latest Science-Based Nutrition Information';
          break;
        case 'recipes':
          title = 'Simple, Healthy Recipes';
          break;
        case 'restaurants':
          title = 'Ordering Guides | Restaurants';
          break;
        case 'sleep':
        case 'sleeping':
          title = 'Science-Based Sleep Articles';
          break;
        case 'social-situations':
          title = 'Common Social Situations';
          break;
        case 'stress':
          title = 'The Impact of Stress on the Mind';
          break;
        case 'weight-loss-programmes':
          title = 'Comparisons of Weight Loss Programmes';
          break;
        default: {
          // Using category name as general purpose title (special cases hardcoded above)
          title = categoryName;
          break;
        }
      }
    }
  }

  title += ' | Second Nature Guides';

  const recentArticleTiles = getRecentArticles(recentPosts).map(article => (
    <ArticleTile
      title={article.title}
      category={article.category}
      href={article.href}
      imageUrl={article.featuredImageUrl}
      key={article.title}
      description={article.description}
      date={article.date}
      columnsPerRowDesktop={2}
    />
  ));

  const baseURL = `/guides/category/${
    parentCategorySlug ? `${parentCategorySlug}/` : ''
  }${categorySlug}`;

  const nextPageHref = recentPosts.pageInfo.hasNextPage
    ? `${baseURL}/after/${recentPosts.pageInfo.endCursor}`
    : null;

  const relativeCanonicalURL = `${baseURL}${
    afterCursor ? `/after/${afterCursor}` : ''
  }`;

  return (
    <>
      <Meta
        title={title}
        metaDescription={metaDescription}
        relativeCanonicalURL={relativeCanonicalURL}
      />
      <NavBar
        utmMedium="category"
        utmCampaign={`${categorySlug.replace(/-/g, '_')}_navbar`}
      />
      <CategoryNavigation
        allCategories={allCategories}
        categorySlug={categorySlug}
      />
      <div className="px-3 text-blue lg:container lg:m-auto lg:px-0">
        <div className="mb-5 mt-12 text-center lg:mb-10 lg:mt-20">
          <H2>{categoryName}</H2>
          {recentArticleTiles.length === 0 && (
            <H4>{comingSoonArticleType} coming soon!</H4>
          )}
          <div className="mt-4">
            <H5>{metaDescription}</H5>
          </div>
        </div>
        <div className="mb-5 flex flex-wrap justify-between">
          {recentArticleTiles}
        </div>
        {nextPageHref && (
          <ArticleNextButton nextPageHref={nextPageHref}>
            Next {POSTS_PER_PAGE}{' '}
            {(parentCategorySlug || categorySlug).startsWith('recipes')
              ? 'recipes'
              : 'articles'}
          </ArticleNextButton>
        )}
      </div>
      <SignPost
        version="A"
        utmMedium="category"
        utmCampaign={`${categorySlug.replace(/-/g, '_')}_banner`}
      />
      <ReferencesRibbon />
      <Footer />
    </>
  );
};

export default Category;
